import { useMemo, useState, type CSSProperties } from "react";
import { Link, useParams } from "react-router-dom";
import { CAL_BOOK_20MIN_URL } from "../../config/links";
import { useInterest } from "../../hooks/useAnalytics";
import { AGENT_TEMPLATES, type AgentTemplate } from "./mock";
import styles from "./AgentDetail.module.css";

const VALUE_PROPS: Record<string, string[]> = {
  "voice-receptionist": [
    "Picks up calls 24/7 in your brand voice.",
    "Qualifies leads against your ICP, books straight into your calendar.",
    "Sends a structured handoff brief to the team for every booked call.",
  ],
  "inbox-triage": [
    "Sorts inbox by intent — leads, customers, internal, noise.",
    "Drafts replies in your voice; sends after one-click review.",
    "Protects deep work blocks by deferring low-priority threads.",
  ],
};

const fallbackValueProps = (template: AgentTemplate): string[] => [
  `Pre-built ${template.name} agent, tuned to your team's specifics.`,
  "Onboarding call to capture process + tone-of-voice before launch.",
  "Bring-your-own-data: connect existing tools in the first week.",
];

const Hero = ({ template }: { template: AgentTemplate }): JSX.Element => {
  const gradientStyle: CSSProperties = {
    ["--gradient-from" as string]: template.gradientFrom,
    ["--gradient-to" as string]: template.gradientTo,
  };

  return (
    <header className={styles.hero}>
      <div className={styles.heroArt} style={gradientStyle}>
        <img src={template.imageUrl} alt="" className={styles.heroImage} />
      </div>
      <div className={styles.titleBlock}>
        <span className={styles.eyebrow}>// AGENT TEMPLATE</span>
        <h1 className={styles.name}>{template.name}</h1>
        <p className={styles.blurb}>{template.description}</p>
        <div className={styles.tags}>
          {template.tags.map((t) => (
            <span key={t} className={styles.tag}>{t}</span>
          ))}
        </div>
      </div>
    </header>
  );
};

export const AgentDetail = (): JSX.Element => {
  const { slug = "" } = useParams<{ slug: string }>();
  const template: AgentTemplate | undefined = useMemo(
    () => AGENT_TEMPLATES.find((t) => t.slug === slug),
    [slug],
  );

  const { summary, register } = useInterest("agent", slug);
  const [note, setNote] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  if (!template) {
    return (
      <div className={styles.page}>
        <p className={styles.notFound}>
          We couldn't find that agent template.
          <br />
          <Link className={styles.back} to="/dashboard/overview">
            ← Back to dashboard
          </Link>
        </p>
      </div>
    );
  }

  const userRegistered: boolean = summary?.userRegistered === true;
  const interestedCount: number = summary?.interestedCount ?? 0;

  const handleRegister = async (): Promise<void> => {
    setSubmitting(true);
    setError(null);
    const result = await register(note);
    setSubmitting(false);
    if (!result.ok) {
      setError(result.message);
    } else {
      setNote("");
    }
  };

  const valueProps = VALUE_PROPS[template.slug] ?? fallbackValueProps(template);

  return (
    <div className={styles.page}>
      <Link className={styles.back} to="/dashboard/overview">
        ← Back to dashboard
      </Link>

      <Hero template={template} />

      <div className={styles.body}>
        <section className={styles.left}>
          <h2 className={styles.sectionTitle}>What it does on day one</h2>
          <ul className={styles.bullets}>
            {valueProps.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>

          <h2 className={styles.sectionTitle}>How we set it up</h2>
          <p className={styles.paragraph}>
            We pair you with a specialist for a 30-minute kickoff to capture
            your tone of voice, process, and SLA expectations. The template
            ships pre-wired to your tenant; the kickoff is what makes it
            actually sound like <em>you</em>.
          </p>
        </section>

        <aside className={styles.right}>
          <div className={styles.cta}>
            <span className={styles.ctaEyebrow}>// INTEREST</span>
            {userRegistered ? (
              <>
                <p className={styles.ctaText}>
                  <strong>Interest registered.</strong> A specialist will be in
                  touch within one business day.
                </p>
                {interestedCount > 1 ? (
                  <p className={styles.ctaCount}>
                    {interestedCount} customers asking for this so far.
                  </p>
                ) : null}
              </>
            ) : (
              <>
                <p className={styles.ctaText}>
                  Want this agent? Tell us a little about how you'd use it and
                  we'll line up the kickoff.
                </p>
                <label className={styles.label}>
                  <span>How would you use it? (optional)</span>
                  <textarea
                    className={styles.textarea}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={`Where would ${template.name} fit in your team?`}
                    rows={3}
                    maxLength={1000}
                  />
                </label>
                <button
                  type="button"
                  className={styles.ctaButton}
                  onClick={() => void handleRegister()}
                  disabled={submitting}
                >
                  {submitting ? "Sending…" : "Register interest"}
                  <span aria-hidden>→</span>
                </button>
                {error ? <p className={styles.error}>{error}</p> : null}
                {interestedCount > 0 ? (
                  <p className={styles.ctaCount}>
                    {interestedCount} customers waiting on this.
                  </p>
                ) : null}
              </>
            )}
          </div>

          <a
            className={styles.scope}
            href={CAL_BOOK_20MIN_URL}
            target="_blank"
            rel="noreferrer noopener"
          >
            <span>Prefer to scope on a call? Book 20-min</span>
            <span aria-hidden>→</span>
          </a>
        </aside>
      </div>
    </div>
  );
};
